import { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where,
  Timestamp,
  addDoc 
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

export interface RatingWeights {
  experiencia: number;
  cursos: number;
  opiniones: number;
  participacion: number;
  perfil: number;
}

export interface UserRatingData {
  userId: string;
  userType: 'asesor' | 'asociado';
  anosExperiencia: number;
  cursosCompletados: number;
  promedioEstrellas: number;
  numPropiedades: number;
  perfilCompleto: boolean;
  calificacionActual?: number;
  ultimaActualizacion?: Date;
}

export interface RatingBreakdown {
  experiencia: number;
  cursos: number;
  opiniones: number;
  participacion: number;
  perfil: number;
}

export interface CalculatedRating {
  calificacionFinal: number;
  breakdown: RatingBreakdown;
  nivel: string;
  ultimaActualizacion: Date;
}

const DEFAULT_WEIGHTS: RatingWeights = {
  experiencia: 0.2,
  cursos: 0.2,
  opiniones: 0.25,
  participacion: 0.2,
  perfil: 0.15
};

export const useRating = () => {
  const [weights, setWeights] = useState<RatingWeights>(DEFAULT_WEIGHTS);
  const [loading, setLoading] = useState(false);

  // Cargar pesos desde configuración (si existe)
  useEffect(() => {
    const loadWeights = async () => {
      try {
        const configQuery = await getDocs(collection(db, 'configuracion_rating'));
        if (!configQuery.empty) {
          const configData = configQuery.docs[0].data();
          if (configData.pesos) {
            setWeights(configData.pesos);
          }
        }
      } catch (error) {
        console.warn('No se pudieron cargar los pesos de configuración, usando valores por defecto');
      }
    };
    loadWeights();
  }, []);

  const calculateUserRating = async (userId: string, userType: 'asesor' | 'asociado'): Promise<CalculatedRating | null> => {
    setLoading(true);
    try {
      // Obtener datos del usuario
      const userQuery = await getDocs(
        query(collection(db, userType), where(userType === 'asesor' ? 'asesor_id' : 'asociado_id', '==', userId))
      );
      
      if (userQuery.empty) {
        throw new Error(`Usuario ${userType} no encontrado`);
      }

      const userData = userQuery.docs[0].data();
      const userDocId = userQuery.docs[0].id;

      // 1. Años de experiencia
      const anosExp = userData.anos_experiencia || userData.años_experiencia || 0;
      let expScore = 0;
      if (anosExp >= 10) expScore = 100;
      else if (anosExp >= 6) expScore = 75;
      else if (anosExp >= 3) expScore = 50;
      else if (anosExp > 0) expScore = 25;

      // 2. Cursos completados
      const cursosQuery = await getDocs(
        query(
          collection(db, 'inscripcion'), 
          where('perfil_id', '==', userId),
          where('completado', '==', true)
        )
      );
      const numCursos = cursosQuery.size;
      let cursoScore = 0;
      if (numCursos >= 11) cursoScore = 100;
      else if (numCursos >= 7) cursoScore = 75;
      else if (numCursos >= 4) cursoScore = 50;
      else if (numCursos > 0) cursoScore = 25;

      // 3. Promedio de opiniones aprobadas
      const opinionesQuery = await getDocs(
        query(
          collection(db, 'calificacion'),
          where('perfil_id', '==', userId),
          where('estatus', '==', 'aprobada')
        )
      );
      
      let promedioEstrellas = 0;
      if (!opinionesQuery.empty) {
        const totalEstrellas = opinionesQuery.docs.reduce((sum, doc) => {
          return sum + (doc.data().estrellas || 0);
        }, 0);
        promedioEstrellas = totalEstrellas / opinionesQuery.size;
      }
      const opinionScore = Math.round((promedioEstrellas / 5) * 100);

      // 4. Participación activa (propiedades)
      const propiedades = userData.propiedad_id || [];
      const numPropiedades = Array.isArray(propiedades) ? propiedades.length : 0;
      let partScore = 0;
      if (numPropiedades >= 10) partScore = 100;
      else if (numPropiedades >= 5) partScore = 50;
      else if (numPropiedades > 0) partScore = 25;

      // 5. Perfil completo
      const perfilCompleto = !!(
        userData.foto && 
        userData.biografia && 
        userData.telefono && 
        userData.inmobiliaria_id
      );
      const perfilScore = perfilCompleto ? 100 : 0;

      // Cálculo final
      const calificacionFinal = Math.round(
        expScore * weights.experiencia +
        cursoScore * weights.cursos +
        opinionScore * weights.opiniones +
        partScore * weights.participacion +
        perfilScore * weights.perfil
      );

      const breakdown: RatingBreakdown = {
        experiencia: expScore,
        cursos: cursoScore,
        opiniones: opinionScore,
        participacion: partScore,
        perfil: perfilScore
      };

      // Determinar nivel
      let nivel = '';
      if (calificacionFinal >= 80) nivel = 'Excelente';
      else if (calificacionFinal >= 65) nivel = 'Bueno';
      else if (calificacionFinal >= 45) nivel = 'Regular';
      else nivel = 'Bajo';

      // Actualizar calificación en el perfil del usuario
      await updateDoc(doc(db, userType, userDocId), {
        calificacion: calificacionFinal,
        nivel_calificacion: nivel,
        ultima_actualizacion_rating: Timestamp.now(),
        breakdown_rating: breakdown
      });

      const result: CalculatedRating = {
        calificacionFinal,
        breakdown,
        nivel,
        ultimaActualizacion: new Date()
      };

      setLoading(false);
      return result;

    } catch (error) {
      setLoading(false);
      console.error('Error calculando rating:', error);
      return null;
    }
  };

  const updateWeights = async (newWeights: RatingWeights) => {
    try {
      // Validar que los pesos sumen 100%
      const total = Object.values(newWeights).reduce((sum, weight) => sum + weight, 0);
      if (Math.abs(total - 1) > 0.01) {
        throw new Error('Los pesos deben sumar exactamente 100%');
      }

      // Guardar en Firestore
      const configQuery = await getDocs(collection(db, 'configuracion_rating'));
      
      if (configQuery.empty) {
        // Crear nueva configuración
        await addDoc(collection(db, 'configuracion_rating'), {
          pesos: newWeights,
          actualizado_por: 'admin',
          fecha_actualizacion: Timestamp.now()
        });
      } else {
        // Actualizar configuración existente
        const configDocId = configQuery.docs[0].id;
        await updateDoc(doc(db, 'configuracion_rating', configDocId), {
          pesos: newWeights,
          actualizado_por: 'admin',
          fecha_actualizacion: Timestamp.now()
        });
      }

      setWeights(newWeights);
      return true;
    } catch (error) {
      console.error('Error actualizando pesos:', error);
      throw error;
    }
  };

  const recalculateAllRatings = async (): Promise<{ success: number; errors: number }> => {
    setLoading(true);
    let success = 0;
    let errors = 0;

    try {
      // Recalcular para todos los asesores
      const asesoresQuery = await getDocs(collection(db, 'asesor'));
      for (const doc of asesoresQuery.docs) {
        try {
          const userData = doc.data();
          const userId = userData.asesor_id;
          if (userId) {
            await calculateUserRating(userId, 'asesor');
            success++;
          }
        } catch (error) {
          errors++;
          console.error(`Error recalculando asesor ${doc.id}:`, error);
        }
      }

      // Recalcular para todos los asociados
      const asociadosQuery = await getDocs(collection(db, 'asociado'));
      for (const doc of asociadosQuery.docs) {
        try {
          const userData = doc.data();
          const userId = userData.asociado_id;
          if (userId) {
            await calculateUserRating(userId, 'asociado');
            success++;
          }
        } catch (error) {
          errors++;
          console.error(`Error recalculando asociado ${doc.id}:`, error);
        }
      }

      setLoading(false);
      return { success, errors };
    } catch (error) {
      setLoading(false);
      console.error('Error en recálculo masivo:', error);
      return { success, errors: errors + 1 };
    }
  };

  return {
    weights,
    loading,
    calculateUserRating,
    updateWeights,
    recalculateAllRatings
  };
};